import React, { useEffect, useState } from 'react';
import ShareButton from './ShareButton.js';
import { useAdminAuth } from '../../../context/AdminAuthContext';
import LinkifyText from './LinkifyText.js';
import '../../css/MemberCard.css';

const MemberCard = () => {
    const [members, setMembers] = useState([]);
    const [expanded, setExpanded] = useState({});
    const [showConfirmCloseModal, setShowConfirmCloseModal] = useState(false);
    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
    const [editingMember, setEditingMember] = useState(null);
    const [deletingMember, setDeletingMember] = useState(null);
    const { isAdmin } = useAdminAuth();

    useEffect(() => {
        fetch('https://grupo.us.es/ligaincom/api/member_func.php')
            .then(response => response.json())
            .then(data => {
                if (data.error) {
                    console.error("Error al obtener integrantes:", data.error);
                } else {
                    setMembers(data);
                    setExpanded(data.reduce((acc, member) => ({ ...acc, [member.idmember]: false }), {}));
                }
            })
            .catch(error => console.error("Error en la solicitud:", error));
    }, []);

    const handleEdit = (member) => {
        setEditingMember(member);
    };

    const handleDelete = (member) => {
        setDeletingMember(member);
        setShowConfirmDeleteModal(true); // Abrir modal de confirmación de borrado
    };

    const confirmDelete = async () => {
        try {
            const response = await fetch('https://grupo.us.es/ligaincom/api/delete_member.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ id: deletingMember.idmember }),
            });

            if (response.ok) {
                setMembers(members.filter(member => member.idmember !== deletingMember.idmember));
                setDeletingMember(null);
                setShowConfirmDeleteModal(false); // Cerrar modal
            } else {
                console.error('Error al borrar el integrante');
            }
        } catch (error) {
            console.error('Error en la solicitud:', error);
        }
    };

    const cancelDelete = () => {
        setDeletingMember(null);
        setShowConfirmDeleteModal(false); // Cerrar modal de confirmación de borrado
    };

    const handleCloseModal = () => {
        setShowConfirmCloseModal(true);
    };

    const handleConfirmClose = () => {
        setEditingMember(null);
        setShowConfirmCloseModal(false);
    };

    const handleCancelClose = () => {
        setShowConfirmCloseModal(false);
    };

    const handleSave = async () => {
        const formData = new FormData();
        const name = document.getElementById('name').value;
        const surname = document.getElementById('surname').value;
        const role = document.getElementById('role').value;
        const bio = document.getElementById('bio').value;
        const scholar = document.getElementById('scholar').value;
        const orcid = document.getElementById('orcid').value;
        const prisma = document.getElementById('prisma').value;
        const imgFile = document.getElementById('img_url').files[0];
        const imgAlt = document.getElementById('img_alt').value;

        formData.append('id', editingMember.idmember);
        formData.append('name', name);
        formData.append('surname', surname);
        formData.append('role', role);
        formData.append('bio', bio);
        formData.append('scholar', scholar);
        formData.append('orcid', orcid);
        formData.append('prisma', prisma);
        formData.append('img_alt', imgAlt);
        if (imgFile) {
            formData.append('img_url', imgFile);
        }

        try {
            const response = await fetch('https://grupo.us.es/ligaincom/api/update_member.php', {
                method: 'POST',
                body: formData,
            });

            if (response.ok) {
                const updatedMembers = members.map(member =>
                    member.idmember === editingMember.idmember
                        ? {
                              ...member,
                              name,
                              surname,
                              role,
                              bio,
                              scholar,
                              orcid,
                              prisma,
                              img_url: imgFile ? imgFile.name : member.img_url,
                              imgAlt,
                          }
                        : member
                );
                setMembers(updatedMembers);
                setEditingMember(null);
            } else {
                console.error('Error al actualizar el integrante');
            }
        } catch (error) {
            console.error('Error en la solicitud:', error);
        }
        window.location.reload();
    };

    const formatDate = (dateString) => {
        const options = {
            year: '2-digit',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        };
        return new Date(dateString).toLocaleString('es-ES', options);
    };

    return (
        <div className="members-cards-container">
            {members.map((member) => {
                const imgSrc = member.img_url
                    ? `https://grupo.us.es/ligaincom/public/uploads/images/${member.img_url}`
                    : 'https://grupo.us.es/ligaincom/public/uploads/images/comic.jpg';

                return (
                    <div
                        className={`members-card ${expanded[member.idmember] ? 'expanded' : ''}`}
                        key={member.idmember}
                        id={`card-${member.idmember}`}
                    >
                        <h5 className="members-card-title">{member.name} {member.surname}</h5>
                        <img className="member-img" src={imgSrc} alt={member.img_alt || 'Imagen por defecto'} />
                        <p className="members-card-text">
                                <small className="members-text-muted">{member.role}</small>
                            </p>
                        <div className="members-card-body bio">
                            <LinkifyText text={member.bio} />
                            {/* Redes sociales */}
                            <div className="members-social-icons">
                                {member.scholar && (
                                    <a href={member.scholar} target="_blank" rel="noopener noreferrer">
                                        <img src="https://grupo.us.es/ligaincom/public/uploads/images/scholar.png" 
                                        alt="Google Scholar" style={{ width: '55px', height: '50px' }} />
                                    </a>
                                )}
                                {member.orcid && (
                                    <a href={member.orcid} target="_blank" rel="noopener noreferrer">
                                        <img src="https://grupo.us.es/ligaincom/public/uploads/images/orcid.png" 
                                        alt="ORCID" style={{ width: '60px', height: '40px' }} />
                                    </a>
                                )}
                                {member.prisma && (
                                    <a href={member.prisma} target="_blank" rel="noopener noreferrer">
                                        <img src="https://grupo.us.es/ligaincom/public/uploads/images/prisma.png" 
                                        alt="Prisma" style={{ width: '60px', height: '40px' }} />
                                    </a>
                                )}
                            </div>

                            {isAdmin && (
                                <div style={{ marginTop: '10px' }}>
                                    <button
                                        onClick={() => handleEdit(member)}
                                        style={{
                                            marginRight: '10px',
                                            padding: '5px 10px',
                                            backgroundColor: '#ffc107',
                                            border: 'none',
                                            borderRadius: '5px',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        Editar
                                    </button>
                                    <button
                                        onClick={() => handleDelete(member)}
                                        style={{
                                            padding: '5px 10px',
                                            backgroundColor: '#dc3545',
                                            border: 'none',
                                            borderRadius: '5px',
                                            cursor: 'pointer',
                                            color: 'white',
                                        }}
                                    >
                                        Borrar
                                    </button>
                                </div>
                            )}
                        </div >
                        <hr />
                    </div>
                );
            })}

            {/* Modal para editar el integrante */}
            {editingMember && (
                <div className="modal show" style={{ display: 'block' }} tabIndex="-1">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Editar integrante</h5>
                                <button type="button" className="btn-close" aria-label="Close" onClick={handleCloseModal}></button>
                            </div>
                            <div className="modal-body">
                                <form>
                                    <label htmlFor="name">Nombre:</label><br />
                                    <input type="text" id="name" name="name" defaultValue={editingMember.name} /><br />
                                    <label htmlFor="name">Apellidos:</label><br />
                                    <input type="text" id="surname" name="surname" defaultValue={editingMember.surname} /><br />
                                    <label htmlFor="role">Rol:</label><br />
                                    <input type="text" id="role" name="role" defaultValue={editingMember.role} /><br />
                                    <label htmlFor="bio">Bio:</label><br />
                                    <textarea rows="8" cols="35" id="bio" name="bio" defaultValue={editingMember.bio} /><br />
                                    <label htmlFor="scholar">Google Scholar:</label><br />
                                    <input type="text" id="scholar" name="scholar" defaultValue={editingMember.scholar} /><br />
                                    <label htmlFor="orcid">Orcid:</label><br />
                                    <input type="text" id="orcid" name="orcid" defaultValue={editingMember.orcid} /><br />
                                    <label htmlFor="prisma">Prisma:</label><br />
                                    <input type="text" id="prisma" name="prisma" defaultValue={editingMember.prisma} /><br />
                                    <label htmlFor="img_url">Imagen:</label><br />
                                    <input type="file" id="img_url" name="img_url" accept="image/*" /><br />
                                    <label htmlFor="img_alt">Texto alternativo de la imagen:</label><br />
                                    <input type="text" id="img_alt" name="img_alt" defaultValue={editingMember.img_alt}/><br />
                                </form>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>Cancelar</button>
                                <button type="button" className="btn btn-primary" onClick={handleSave}>Guardar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de confirmación de cierre */}
            {showConfirmCloseModal && (
                <div className="modal show" style={{ display: 'block' }} tabIndex="-1">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Confirmar cierre</h5>
                                <button type="button" className="btn-close" aria-label="Close" onClick={handleCancelClose}></button>
                            </div>
                            <div className="modal-body">
                                <p>¿Quieres abandonar la edición? Se perderán los cambios no guardados.</p>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={handleCancelClose}>Volver</button>
                                <button type="button" className="btn btn-primary" onClick={handleConfirmClose}>Salir</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de confirmación de borrado */}
            {showConfirmDeleteModal && (
                <div className="modal show" style={{ display: 'block' }} tabIndex="-1">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Confirmar borrado</h5>
                                <button type="button" className="btn-close" aria-label="Close" onClick={cancelDelete}></button>
                            </div>
                            <div className="modal-body">
                                <p>¿Estás seguro de que deseas borrar el integrante {deletingMember?.name} {deletingMember?.surname}?</p>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={cancelDelete}>Cancelar</button>
                                <button type="button" className="btn btn-danger" onClick={confirmDelete}>Borrar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MemberCard;
